'use client';

import { useEffect, useState } from 'react';
import type { CatalogNavigationItem } from '../lib/catalog-navigation';
import { independentCatalogPage } from './catalog-independent/pages/registry';
import GenericCatalogDetail, { type Entry } from './generic-catalog-detail';
import HeroDetailRenderer from './hero-details/registry';
import type { GameConfig } from '../lib/game-config';
import type { HeroData } from '../lib/heroes/types';
import { heroToDirectoryEntry } from '../lib/heroes/directory-entry';
import './legacy-shell-overrides.css';

type Props = {
  game: GameConfig;
  catalog: CatalogNavigationItem;
  itemId: string;
  route: string;
  startAtContent?: boolean;
  initialContentSection?: 'skills' | 'archive';
  initialSectionOffset?: number;
};

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | {
      status: 'ready';
      summary: Entry;
      detail: Entry;
      heroDirectory?: ReturnType<typeof heroToDirectoryEntry>[];
    };

/** 通用/独立图鉴详情：先出框架，再客户端拉 Go。 */
export default function GenericCatalogDetailClient({
  game,
  catalog,
  itemId,
  route,
  startAtContent,
  initialContentSection,
  initialSectionOffset,
}: Props) {
  const [state, setState] = useState<LoadState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const base = `/api/games/${encodeURIComponent(game.key)}/catalogs/${encodeURIComponent(catalog.catalogKey)}/generic/items`;
        const [listRes, detailRes] = await Promise.all([
          fetch(base, { cache: 'no-store', credentials: 'same-origin' }),
          fetch(`${base}?id=${encodeURIComponent(itemId)}`, {
            cache: 'no-store',
            credentials: 'same-origin',
          }),
        ]);
        if (!detailRes.ok) {
          throw new Error(detailRes.status === 404 ? '条目不存在' : '图鉴加载失败');
        }
        const detail = (await detailRes.json()) as Entry;
        let summary: Entry | undefined;
        if (listRes.ok) {
          const list = (await listRes.json()) as { items?: Entry[] };
          summary = (list.items || []).find(
            (item) => item && String(item.id) === itemId,
          );
        }
        if (!summary) summary = { id: itemId, name: detail.name || detail.title };

        let heroDirectory: ReturnType<typeof heroToDirectoryEntry>[] | undefined;
        if (game.key === 'guardian-tales' && catalog.catalogKey === 'heroes') {
          const heroesRes = await fetch(
            `/api/games/${encodeURIComponent(game.key)}/catalog-heroes`,
            { cache: 'no-store', credentials: 'same-origin' },
          );
          if (heroesRes.ok) {
            const payload = (await heroesRes.json()) as { heroes?: HeroData[] };
            heroDirectory = (payload.heroes || []).map(heroToDirectoryEntry);
          }
        }

        if (!cancelled) {
          const title = String(
            detail.name || detail.title || summary.name || '图鉴条目',
          );
          document.title = `${title}｜${catalog.name}｜${game.name} Wiki`;
          setState({ status: 'ready', summary, detail, heroDirectory });
        }
      } catch (error) {
        if (!cancelled) {
          setState({
            status: 'error',
            message: error instanceof Error ? error.message : '图鉴加载失败',
          });
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [catalog.catalogKey, catalog.name, game.key, game.name, itemId]);

  if (state.status === 'ready') {
    if (catalog.updatePolicy === 'custom') {
      const IndependentPage = independentCatalogPage(game.key, catalog.catalogKey);
      if (IndependentPage) {
        return (
          <IndependentPage
            game={game.domain}
            catalogName={catalog.name}
            summary={state.summary}
            detail={state.detail}
          />
        );
      }
    }

    if (game.key === 'guardian-tales' && catalog.catalogKey === 'heroes') {
      const sourceId = state.detail.sourceId;
      if (typeof sourceId === 'string' && sourceId) {
        const hero = { ...state.detail, id: sourceId } as unknown as HeroData;
        return (
          <HeroDetailRenderer
            game={game}
            hero={hero}
            directory={state.heroDirectory || []}
            startAtContent={startAtContent}
            initialContentSection={initialContentSection}
            initialSectionOffset={initialSectionOffset}
          />
        );
      }
    }

    return (
      <GenericCatalogDetail
        game={game.domain}
        catalogName={catalog.name}
        summary={state.summary}
        detail={state.detail}
        itemRoute={`/${game.domain}/${route}`}
      />
    );
  }

  return (
    <main className="hero-detail-shell" aria-busy={state.status === 'loading'}>
      <div className="hero-detail-shell-frame">
        <header className="hero-detail-shell-header">
          <p className="hero-detail-shell-eyebrow">
            {game.name} · {catalog.name}
          </p>
          <h1>{state.status === 'error' ? '加载失败' : '图鉴详情'}</h1>
          <p
            className="hero-detail-shell-status"
            role={state.status === 'error' ? 'alert' : 'status'}
          >
            {state.status === 'error' ? state.message : '条目资料加载中…'}
          </p>
        </header>
        <div className="hero-detail-shell-grid" aria-hidden="true">
          <div className="hero-detail-shell-portrait guide-skeleton" />
          <div className="hero-detail-shell-panel">
            <span className="guide-skeleton guide-skeleton-line" />
            <span className="guide-skeleton guide-skeleton-line" />
            <span className="guide-skeleton guide-skeleton-line is-short" />
            <span className="guide-skeleton guide-skeleton-block" />
          </div>
        </div>
      </div>
    </main>
  );
}
